import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

export interface Subscription {
  id: string;
  planId: string;
  siteQuotaLimit: number;
  siteQuotaUsed: number;
  fleetQuotaLimit: number;
  fleetQuotaUsed: number;
  startDate: string;
  endDate?: string | null;
  status: string;
  clientSecret?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeScheduleId?: string | null;
}

export interface User {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: any;
}

interface CreateSubscriptionData {
  planId: string;
  paymentMethodId: string;
  paymentType?: string;
  cardNumber?: string;
}

interface CreateSubscriptionResponse {
  subscription?: Subscription;
  clientSecret?: string;
  subscriptionId?: string;
  invoiceId?: string;
  status?: string;
  requiresPaymentIntent?: boolean;
  paymentIntentId?: string;
}

interface UpdateSubscriptionData {
  planId: string;
  paymentMethodId?: string;
  paymentType?: string;
  cardNumber?: string;
}

interface ConfirmPaymentData {
  subscriptionId: string;
  paymentMethodId: string;
}

interface ConfirmPaymentResponse {
  clientSecret?: string;
  subscriptionId?: string;
  paymentIntentId?: string;
}

export interface SubscriptionResponseData {
  userId: string;
  email: string;
  firstname: string;
  lastname: string;
  subscriptionId: string;
  planId: string;
  planName: string;
  subscriptionDetails: {
    siteQuotaLimit: number;
    siteQuotaUsed: number;
    fleetQuotaLimit: number;
    fleetQuotaUsed: number;
    startDate: string;
    endDate?: string | null;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    status: string;
    stripeScheduleId?: string | null;
  };
}

export interface SubscriptionDetailsResponseData {
  planName: string;
  siteQuotaLimit: number;
  siteQuotaUsed: number;
  fleetQuotaLimit: number;
  fleetQuotaUsed: number;
  planDescription: string;
  planPrice: number;
  planFeatures: string[];
}

export interface BillingDetails {
  billingDetailsId: string;
  paymentMethod: string;
  cardNumber: string | null;
  billingEmail: string;
  amount: number;
  renewalDate: string;
  createdAt: string;
  updatedAt: string;
  planName: string;
}

export interface BillingHistory {
  billingHistoryId: string;
  subscriptionId: string;
  amount: number;
  description: string;
  stripeInvoiceId: string;
  status: string;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  invoiceId: string;
  amount: number;
  status: string;
  createdAt: string;
  pdfUrl: string | null;
  description: string;
}

export interface DownloadInvoiceResponse {
  pdfUrl: string;
}

export interface ScheduledSubscriptionDetails {
  subscriptionId: string;
  currentStatus: string;
  currentPlanId: string;
  currentSiteQuotaLimit: number;
  currentSiteQuotaUsed: number;
  currentFleetQuotaLimit: number;
  currentFleetQuotaUsed: number;
  startDate: string;
  stripeSubscriptionId: string;
}

export interface ScheduledPlanDetails {
  planId: string;
  planName: string;
  duration: string;
  price: number;
  siteQuotaLimit: number;
  fleetQuotaLimit: number;
  description: string;
  features: string[];
  scheduledStartDate: string | null;
  scheduleId: string;
}

interface CancelSubscriptionResponse {
  cancelAt: string;
}

// NEW: Interface for Financial Metrics Response
export interface FinancialMetricsResponse {
  stripeRevenueCollected: number;
  outstandingInvoices: { count: number; amount: number };
  failedPayments: { count: number; value: number };
}

// NEW: Interface for Recurring Revenue Response
export interface RecurringRevenueResponse {
  mrr: number;
  arr: number;
}

// CREATE SUBSCRIPTION
export const createSubscriptionAction = createAsyncThunk<
  ApiResponse<CreateSubscriptionResponse>,
  CreateSubscriptionData,
  { rejectValue: string }
>("subscription/createSubscription", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<
      ApiResponse<CreateSubscriptionResponse>
    >("/subscription", formData);

    if (response.data.success) {
      toast.success(
        response.data.message || "Subscription created successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to create subscription");
      return rejectWithValue(
        response.data.message || "Failed to create subscription"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET SUBSCRIPTION BY USER ID
export const getSubscriptionByUserId = createAsyncThunk<
  ApiResponse<SubscriptionResponseData>,
  void,
  { rejectValue: string }
>("subscription/getSubscription", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<SubscriptionResponseData>
    >("/subscription");

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch subscription"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

// GET SUBSCRIPTION DETAILS
export const getSubscriptionDetailsAction = createAsyncThunk<
  ApiResponse<SubscriptionDetailsResponseData>,
  void,
  { rejectValue: string }
>("subscription/getSubscriptionDetails", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<SubscriptionDetailsResponseData>
    >("/subscription/details");

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch subscription details"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

// UPDATE SUBSCRIPTION
export const updateSubscriptionAction = createAsyncThunk<
  ApiResponse<Subscription>,
  UpdateSubscriptionData,
  { rejectValue: string }
>("subscription/updateSubscription", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.put<ApiResponse<Subscription>>(
      "/subscription",
      formData
    );

    if (response.data.success) {
      toast.success(
        response.data.message || "Subscription updated successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to update subscription");
      return rejectWithValue(
        response.data.message || "Failed to update subscription"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// CANCEL SUBSCRIPTION
export const cancelSubscriptionAction = createAsyncThunk<
  ApiResponse<CancelSubscriptionResponse>,
  void,
  { rejectValue: string }
>("subscription/cancelSubscription", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<
      ApiResponse<CancelSubscriptionResponse>
    >("/subscription");

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to cancel subscription");
      return rejectWithValue(
        response.data.message || "Failed to cancel subscription"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// CONFIRM SUBSCRIPTION PAYMENT
export const confirmSubscriptionPaymentAction = createAsyncThunk<
  ApiResponse<ConfirmPaymentResponse>,
  ConfirmPaymentData,
  { rejectValue: string }
>(
  "subscription/confirmSubscriptionPayment",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<
        ApiResponse<ConfirmPaymentResponse>
      >("/subscription/confirm-payment", formData);

      if (response.data.success) {
        toast.success(
          response.data.message || "Payment confirmed successfully"
        );
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to confirm payment");
        return rejectWithValue(
          response.data.message || "Failed to confirm payment"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as { message?: string }).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET BILLING DETAILS BY USER ID
export const getBillingDetailsByUserId = createAsyncThunk<
  ApiResponse<BillingDetails[]>,
  void,
  { rejectValue: string }
>("subscription/getBillingDetails", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ApiResponse<BillingDetails[]>>(
      "/subscription/billing-details"
    );

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch billing details"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

// GET BILLING HISTORY
export const getBillingHistoryAction = createAsyncThunk<
  ApiResponse<BillingHistory[]>,
  void,
  { rejectValue: string }
>("subscription/getBillingHistory", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ApiResponse<BillingHistory[]>>(
      "/subscription/billing-history"
    );

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch billing history"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

export const getBillingHistoryByUserId = createAsyncThunk<
  ApiResponse<BillingHistory[]>,
  string,
  { rejectValue: string }
>("subscription/getBillingHistoryByUserId", async (userId, { rejectWithValue }) => {
    try {
      const response = await createApiClient.get<ApiResponse<BillingHistory[]>>(
        `/subscription/${userId}/billing-history`
      );

      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch billing history"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as { message?: string }).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
});

// GET USER INVOICES LIST
export const getUserInvoicesListAction = createAsyncThunk<
  ApiResponse<Invoice[]>,
  void,
  { rejectValue: string }
>("subscription/getUserInvoicesList", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<ApiResponse<Invoice[]>>(
      "/subscription/invoices"
    );

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch invoices list"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

// DOWNLOAD INVOICE
export const downloadInvoiceAction = createAsyncThunk<
  ApiResponse<DownloadInvoiceResponse>,
  string,
  { rejectValue: string }
>("subscription/downloadInvoice", async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<DownloadInvoiceResponse>
    >(`/subscription/invoice/${invoiceId}`);

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to fetch invoice PDF URL");
      return rejectWithValue(
        response.data.message || "Failed to fetch invoice PDF URL"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET SCHEDULED PLAN
export const getScheduledPlanAction = createAsyncThunk<
  ApiResponse<{
    subscriptionDetails: ScheduledSubscriptionDetails;
    scheduledPlanDetails: ScheduledPlanDetails;
  }>,
  void,
  { rejectValue: string }
>("subscription/getScheduledPlan", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<{
        subscriptionDetails: ScheduledSubscriptionDetails;
        scheduledPlanDetails: ScheduledPlanDetails;
      }>
    >("/subscription/scheduled-plan");

    if (response.data.success) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch scheduled plan"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return rejectWithValue(errorMessage);
  }
});

// GET FINANCIAL METRICS (Admin only)
export const getFinancialMetricsAction = createAsyncThunk<
  ApiResponse<FinancialMetricsResponse>,
  void,
  { rejectValue: string }
>("subscription/getFinancialMetrics", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<FinancialMetricsResponse>
    >("/subscription/financial-metrics");

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to fetch financial metrics");
      return rejectWithValue(
        response.data.message || "Failed to fetch financial metrics"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET RECURRING REVENUE (Admin only)
export const getRecurringRevenueAction = createAsyncThunk<
  ApiResponse<RecurringRevenueResponse>,
  void,
  { rejectValue: string }
>("subscription/getRecurringRevenue", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<
      ApiResponse<RecurringRevenueResponse>
    >("/subscription/recurring-revenue");

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to fetch recurring revenue");
      return rejectWithValue(
        response.data.message || "Failed to fetch recurring revenue"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";

    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as { message?: string }).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
